import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import PackageChooser from '../../../PackageChooser';
import NavBar from '../../../PackageDisplay/NavBar';
import routes from '../../../../routes';
import { useCustomer } from '../../../../contexts/CustomerContext';
import useOfferCoupon from '../../../../hooks/useOfferCoupon';
import useApplyOfferToCart from '../../../../hooks/useApplyOfferToCart';
import usePricelist from '../../../../hooks/usePricelist';
import useHolidayPricelist from '../../../../hooks/useHolidayPricelist';
import useLocalStorage from '../../../../hooks/useLocalStorage';
import QueryKeys from '../../../../utils/QueryKeys';
import { isBuyAllDigital, addBuyAllToCart } from '../../../../utils/buyAllDigital';

// The $0 "yearbook" Option, identified the way the rest of the senior flow does: the
// Option whose name includes "yearbook".
const isYearbookOption = (option) => option?.name?.toLowerCase().includes('yearbook');

/**
 * The senior flavour of PackageChooser. Two things are true only of seniors:
 *
 *  - The $0 yearbook line is NOT a package to choose here — it's already in the cart from
 *    the gallery step — so its Option is dropped from the grid.
 *  - A senior may want nothing but their yearbook photo, so there's a way straight to
 *    review/checkout with that $0 line alone.
 *
 * Everything else (pricing, cards, previews) is the shared screen — each card composites
 * the student's own poses via AbsolutePosImgs, so no hero-photo prop is needed here.
 */
export default function SeniorPackageChooser({
  packageId, onSelect, onSkip,
}) {
  const { customer } = useCustomer();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { data: pricelist } = usePricelist(packageId);
  // Season the buy-all endpoint uses to locate the student's senior images. Set at
  // access-code entry; fall back to the pricelist season so it is never blank.
  const [currentSeason] = useLocalStorage(`currentSeason-${customer?.currentStudent?.id}`);
  // The "Prints / Digital Files" tab goes to HolidayBuilder, which renders null when the
  // school has no gifts pricelist — hide the tab in that case rather than send the customer
  // to a blank page. Same guard Builder.jsx:102 already applies before that route. Shares
  // react-query's cache with Builder's identical call, so this adds no extra request.
  const { data: giftPricelist } = useHolidayPricelist(customer?.currentSchool?.id);
  const [submitting, setSubmitting] = useState(false);

  // The promotional offer to feature for this student right now, resolved by
  // days-since-photographed and scoped to this pricelist so checkout will honor it.
  const { data: offer } = useOfferCoupon(
    customer?.currentStudent?.studentId,
    customer?.currentSchool?.id,
    packageId,
  );
  // Auto-apply it so the discount rides into checkout without the customer typing a code.
  useApplyOfferToCart(offer);

  // A Buy-All-Digital option skips the per-pose builder: create the order package
  // server-side (one digital image per photo, backgrounds auto-cycled) and go straight to
  // checkout, where the customer can still edit any pose. Everything else builds normally.
  const handleSelect = async (option) => {
    if (!isBuyAllDigital(option)) {
      onSelect(option);
      return;
    }
    setSubmitting(true);
    try {
      await addBuyAllToCart({
        cartId: customer.cart.id,
        packageId,
        optionId: option.id,
        studentId: customer.currentStudent.id,
        season: currentSeason || pricelist?.season,
      });
      // Refresh cart-dependent views in the background; don't block navigation on it —
      // the review page fetches the cart fresh on mount.
      queryClient.invalidateQueries({ queryKey: [QueryKeys.cart] });
      history.push(routes.review());
    } catch (error) {
      // Log before alerting, for parity with BuyAllPromo — this is the higher-traffic
      // buy-all entry point, and a silent catch here would hide a failed $-bearing POST
      // from Sentry/console entirely (CLAUDE.md: no silent fallbacks).
      console.error('Buy All Digital failed:', error);
      setSubmitting(false);
      // eslint-disable-next-line no-alert
      window.alert('Something went wrong adding your digital package. Please try again.');
    }
  };

  return (
    <>
      {/* Same tab bar as the undergrad flow: Packages (this screen) + Prints / Digital
          Files, which navigates to the Announcements & Gifts page (as it does for
          undergrad via goToAddons). setSelectedIcon is a no-op — the senior chooser is
          the only in-page view, so "Packages" stays selected. */}
      <NavBar
        selectedIcon="packages"
        setSelectedIcon={() => {}}
        showPrints={!!giftPricelist}
        onPrints={() => history.push(routes.holiday.builder({ optional: true, pid: packageId }))}
      />
      <PackageChooser
        packageId={packageId}
        schoolId={customer.currentSchool.id}
        excludeOption={isYearbookOption}
        onSelect={handleSelect}
        skip={{ label: 'No thanks — just my yearbook photo', onClick: onSkip }}
        offer={offer}
      />
      {submitting && (
        <div className="fixed inset-0 z-[9600] flex items-center justify-center bg-white/70">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-shoob-blue border-t-transparent" />
        </div>
      )}
    </>
  );
}

SeniorPackageChooser.propTypes = {
  packageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSelect: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};
